import {ChipsFilterOption} from '@hconnect/common/types'
import {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
import {mapTasksTableKeyToSortBy, mapTasksSortByToTableKey} from '../mappers'
import {TaskSource, SortOrder, TasksTableSortOptions, TasksTableKey} from '../types'

import {useTasksPageQueryParams} from './useTasksPageQueryParams'

type UseTasksTableFiltersOptions = {
  onFiltersChanged?: () => void
}

export type UseTasksTableFiltersReturn = {
  sourceFilterOptions: ChipsFilterOption<TaskSource>[]
  sourceFilters: TaskSource[]
  onSourceFilterChange: (filters: TaskSource[]) => void
  assignedToMe: boolean
  toggleAssignedToMe: () => void
  createdByMe: boolean
  toggleCreatedByMe: () => void
  showDescriptions: boolean
  toggleShowDescriptions: () => void
  sortOptions?: TasksTableSortOptions
}

export const useTasksTableFilters = ({
  onFiltersChanged
}: UseTasksTableFiltersOptions): UseTasksTableFiltersReturn => {
  const [params, {updateParams}] = useTasksPageQueryParams()

  const [sourceFilters, setSourceFilters] = useState<TaskSource[]>(params.sources ?? [])
  const handleSourceFilterChange = (sources: TaskSource[]) => {
    setSourceFilters(sources)
    updateParams({sources})
    onFiltersChanged?.()
  }
  const [assignedToMe, setAssignedToMe] = useState(params.assignedToMe ?? false)
  const toggleAssignedToMe = () => {
    setAssignedToMe((prev) => {
      const newAssignedToMe = !prev
      updateParams({assignedToMe: newAssignedToMe ? true : undefined})
      return newAssignedToMe
    })
    onFiltersChanged?.()
  }
  const [createdByMe, setCreatedByMe] = useState(params.createdByMe ?? false)
  const toggleCreatedByMe = () => {
    setCreatedByMe((prev) => {
      const newCreatedByMe = !prev
      updateParams({createdByMe: newCreatedByMe ? true : undefined})
      return newCreatedByMe
    })
    onFiltersChanged?.()
  }
  const [showDescriptions, setShowDescriptions] = useState(false)
  const toggleShowDescriptions = () => setShowDescriptions((prev) => !prev)

  const handleSortChange = (tableKey: TasksTableKey, sortOrder?: SortOrder) => {
    const sortBy = sortOrder ? mapTasksTableKeyToSortBy(tableKey) : undefined

    updateParams({sortBy, sortOrder})
    onFiltersChanged?.()
  }

  const {t} = useTranslation(FEATURE_NAMESPACE)
  const sourceFilterOptions: ChipsFilterOption<TaskSource>[] = [
    {
      label: t('tasksTable.filters.label.shift'),
      value: TaskSource.COCKPIT
    },
    {
      label: t('tasksTable.filters.label.pom'),
      value: TaskSource.EASY_POM
    },
    {
      label: t('tasksTable.filters.label.processWorkOrder'),
      value: TaskSource.PROCESS_WORK_ORDER
    },
    {
      label: t('tasksTable.filters.label.machineSentryActions'),
      value: TaskSource.MACHINE_SENTRY_ACTIONS
    },
    {
      label: t('tasksTable.filters.label.oilAnalysisActions'),
      value: TaskSource.OIL_ANALYSIS_ACTIONS
    },
    {
      label: t('tasksTable.filters.label.dalogActions'),
      value: TaskSource.DALOG_ACTIONS
    }
  ]

  return {
    sourceFilterOptions,
    sourceFilters,
    onSourceFilterChange: handleSourceFilterChange,
    assignedToMe,
    toggleAssignedToMe,
    createdByMe,
    toggleCreatedByMe,
    showDescriptions,
    toggleShowDescriptions,
    sortOptions: {
      sortBy: mapTasksSortByToTableKey(params.sortBy) ?? TasksTableKey.DUE_DATE,
      sortOrder: params.sortOrder ?? SortOrder.ASCENDING,
      onSortChange: handleSortChange
    }
  }
}
